import type { Building, ConsumptionGranularity } from "@eisox/api/core-api";
import type { DateFieldUnit } from "@eisox/design-system";

import type { DayRange } from "~/helpers/day-range.helper";

enum ConsumptionTab {
  CHART = "chart",
  HIERARCHY = "hierarchy",
}

enum PeriodPreset {
  SEVEN_DAYS = "7d",
  THREE_MONTHS = "3m",
  TWELVE_MONTHS = "12m",
  FIVE_YEARS = "5y",
}

enum Comparison {
  NONE = "none",
  PREVIOUS_YEAR = "n-1",
  TWO_YEARS_BACK = "n-2",
  THREE_YEARS_BACK = "n-3",
  PREVIOUS_PERIOD = "previousPeriod",
  REFERENCE = "reference",
}

enum BreakdownAlert {
  SUB_METERS_EXCEED = "subMetersExceed",
  HIGH_UNALLOCATED = "highUnallocated",
}

enum SeriesKey {
  CONSUMPTION = "consumption",
  CONSUMPTION_DJU = "consumptionDju",
  COMPARISON = "comparison",
  COMPARISON_DJU = "comparisonDju",
  OBJECTIVE = "objective",
  OBJECTIVE_DJU = "objectiveDju",
}

interface CalendarSpan {
  amount: number;
  unit: "day" | "month" | "year";
}

interface PresetSettings {
  span: CalendarSpan;
  granularity: ConsumptionGranularity;
}

interface GranularitySettings {
  unit: "minute" | "hour" | "day" | "month" | "year";
  label: () => string;
  averageLabel: () => string;
  count: (count: number) => string;
  bucketFormat: string;
  tickFormat: string;
  slotFormat: string;
  calendarUnit: DateFieldUnit;
  maxRange: CalendarSpan;
  retention: CalendarSpan;
}

interface ComparisonSettings {
  label: () => string;
  name: () => string;
  years?: number;
  granularities?: ConsumptionGranularity[];
  objective: boolean;
}

interface ConsumptionPeriod {
  range: DayRange;
  granularity: ConsumptionGranularity;
  preset?: PeriodPreset;
}

interface ChartRow {
  start: string;
  inProgress?: boolean;
  [series: string]: unknown;
}

interface ConsumptionRow extends ChartRow, Partial<Record<SeriesKey, number>> {
  index: number;
  inProgress: boolean;
  comparisonIndex?: number;
}

interface BreakdownRow extends ChartRow {
  [meterId: string]: number | string | boolean | undefined;
}

interface ChartSeries {
  key: string;
  label: string;
  color: string;
  kind: "bar" | "level";
  fadesInProgress?: boolean;
  stackId?: string;
  replaces?: string;
}

type ComparedBuilding = Pick<Building, "timezone" | "weatherStationId" | "referencePeriod">;

interface ComparedRange {
  range: DayRange;
  shift?: CalendarSpan;
}

interface AppliedComparison extends ComparedRange {
  comparison: Comparison;
  objective?: number;
}

interface ChartView {
  period: ConsumptionPeriod;
  granularities: ConsumptionGranularity[];
  degreeDays: boolean;
  comparison?: AppliedComparison;
}

interface BreakdownSegment extends ChartSeries {
  kind: "bar";
  total: number;
  share: number;
}

interface Breakdown {
  total: number;
  subMetersTotal: number;
  unallocated: number;
  unallocatedShare: number;
  segments: BreakdownSegment[];
}

export { BreakdownAlert, Comparison, ConsumptionTab, PeriodPreset, SeriesKey };
export type {
  AppliedComparison,
  Breakdown,
  BreakdownRow,
  BreakdownSegment,
  CalendarSpan,
  ChartRow,
  ChartSeries,
  ChartView,
  ComparedBuilding,
  ComparedRange,
  ComparisonSettings,
  ConsumptionPeriod,
  ConsumptionRow,
  GranularitySettings,
  PresetSettings,
};
